import React, { useState, useEffect, useRef } from "react";
import { Input } from "./input";
import { Button } from "./button";

export default function PasswordModal({
  isOpen,
  onClose,
  onSubmit,
  loading,
  error,
  title = "Verify Password",
  description = "Enter the 4-digit password to reveal customer phone number.",
}) {
  const [password, setPassword] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setPassword("");
      // Focus input after a short delay to ensure modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password.length === 4) {
      onSubmit(password);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
    if (e.key === "Escape") {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="password-modal-overlay" onClick={onClose}>
      <div
        className="password-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-modal-title"
      >
        <div className="password-modal-head">
          <h2 id="password-modal-title" className="password-modal-title">
            {title}
          </h2>
          <button
            className="password-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <div className="password-modal-body">
          <p className="password-modal-description">{description}</p>

          {error && (
            <div className="password-modal-error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="password-input-container">
              <Input
                ref={inputRef}
                type="password"
                inputMode="numeric"
                maxLength={4}
                minLength={4}
                value={password}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setPassword(val);
                }}
                onKeyDown={handleKeyDown}
                placeholder="••••"
                className="password-input"
                disabled={loading}
                autoComplete="off"
                autoFocus
              />
            </div>

            <div className="password-modal-footer">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="password-btn-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={password.length !== 4 || loading}
                className="password-btn-verify"
              >
                {loading ? "Verifying..." : "Verify"}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .password-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, .4);
          display: grid;
          place-items: center;
          z-index: 100;
        }

        .password-modal {
          width: min(400px, 90vw);
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, .2);
          overflow: hidden;
          animation: passwordModalSlideIn 0.2s ease-out;
        }

        @keyframes passwordModalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .password-modal-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid #eee3da;
        }

        .password-modal-title {
          font-size: 18px;
          font-weight: 700;
          color: #2f2b27;
          margin: 0;
        }

        .password-modal-close {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 0;
          background: #f5f5f5;
          cursor: pointer;
          font-size: 16px;
          color: #666;
          display: grid;
          place-items: center;
          transition: all .2s ease;
        }

        .password-modal-close:hover {
          background: #eee;
          color: #333;
        }

        .password-modal-body {
          padding: 24px 20px;
        }

        .password-modal-description {
          color: #666;
          font-size: 14px;
          margin: 0 0 20px 0;
          line-height: 1.5;
        }

        .password-modal-error-message {
          background: #fee2e2;
          color: #dc2626;
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 16px;
        }

        .password-input-container {
          margin-bottom: 20px;
        }

        .password-input {
          width: 100%;
          height: 56px;
          font-size: 24px;
          text-align: center;
          letter-spacing: 12px;
          font-weight: 600;
          border: 2px solid #eee3da;
          border-radius: 12px;
          outline: none;
          transition: all .2s ease;
        }

        .password-input:focus {
          border-color: #ff7a00;
          box-shadow: 0 0 0 4px rgba(255, 122, 0, 0.1);
        }

        .password-input::placeholder {
          letter-spacing: normal;
          color: #ccc;
        }

        .password-input:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        .password-modal-footer {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .password-btn-cancel {
          height: 44px;
          padding: 0 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
        }

        .password-btn-verify {
          height: 44px;
          padding: 0 24px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          background: #ff7a00;
          color: #fff;
          border: 1px solid #ff7a00;
        }

        .password-btn-verify:hover:not(:disabled) {
          background: #e66e00;
          border-color: #e66e00;
        }

        .password-btn-verify:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}

